import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Outlet, NavLink, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { PreviewRoleContext } from '../context/PreviewRoleContext';
import { LevelUpFooter } from './LevelUpFooter';
import { useTelemetry } from '../hooks/useTelemetry';
import {
  LayoutDashboard, FileBarChart2, Users, Activity,
  ChevronRight, LogOut, Settings, RefreshCw, BookOpen, Building2,
  Globe, Award, Link, Database, TrendingUp, ShieldCheck,
  Eye, ChevronDown, X, Send, Package, UserCog, CalendarDays, GraduationCap, Library, Rss, Route, UserCheck, Ticket,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Simulated scopes for the Switch Role preview ───────────────────────────────
// These mirror the scope flags that lupnxtlaravel issues to each role type.
// Navigation-only preview — actual data queries run as the real session user.
const PREVIEW_ROLES = [
  {
    id: 'ms_global_rm',
    label: 'MS Global RM',
    description: 'Microsoft Global Reporting Manager — full access except Admin/Cert Prep',
    scope: {
      type: 'ms_global',
      show_pii: false,
      show_all_up: true,
      show_jumpstart: true,
      show_deal_ready: true,
      show_certification: false,
      show_admin: false,
      show_acceleration: true,
    },
  },
  {
    id: 'ms_pdm',
    label: 'MS PDM RM',
    description: 'Microsoft PDM — sees All-Up, Partners (own), basic reports',
    scope: {
      type: 'pdm',
      show_pii: true,
      show_all_up: true,
      show_jumpstart: false,
      show_deal_ready: false,
      show_certification: false,
      show_admin: false,
    },
  },
  {
    id: 'ms_project_rm',
    label: 'MS Project RM',
    description: 'Microsoft Project RM — limited access, no PII, no partner section',
    scope: {
      type: 'project_rm',
      show_pii: false,
      show_all_up: true,
      show_jumpstart: false,
      show_deal_ready: false,
      show_certification: false,
      show_admin: false,
    },
  },
  {
    id: 'partner_rm',
    label: 'Partner RM',
    description: 'Partner organisation user — Partner Self-Service only',
    scope: {
      type: 'partner',
      show_pii: false,
      show_all_up: false,
      show_jumpstart: false,
      show_deal_ready: false,
      show_certification: false,
      show_admin: false,
    },
  },
];

function MicrosoftLogo() {
  return (
    <svg width="14" height="14" viewBox="0 0 21 21" style={{ flexShrink: 0 }}>
      <rect x="0"  y="0"  width="10" height="10" fill="#f25022" />
      <rect x="11" y="0"  width="10" height="10" fill="#7fba00" />
      <rect x="0"  y="11" width="10" height="10" fill="#00a4ef" />
      <rect x="11" y="11" width="10" height="10" fill="#ffb900" />
    </svg>
  );
}

// Page key → route mapping (used by ms_prog_admin to build nav)
const PAGE_ROUTES = {
  all_up_report:     '/reports/allup',
  completion_report: '/reports/completion',
  course_activity:   '/reports/course-activity',
  learner_activity:  '/reports/learner-activity',
  journey_report:    '/reports/journeys',
  partner_report:    '/reports/by-partner',
  vouchers:          '/reports/vouchers',
  cert_prep:         '/reports/cert-prep',
  jumpstart:         '/reports/jumpstart',
  deal_ready:        '/reports/deal-ready',
  scorm_cloud:       '/reports/scorm-cloud',
  compete_hub:       '/compete-hub',
  skilling_box:      '/reporting/skilling-box',
  partner_dashboard: '/reports/partner-self-service',
  mbr_dashboard:     '/reports/mbr',
  esi_utilisation:   '/reports/esi-utilisation',
};

const PAGE_LABELS = {
  all_up_report: 'All-Up LevelUp', completion_report: 'Insights Cube',
  course_activity: 'Course Activity', learner_activity: 'Learner Activity',
  journey_report: 'Learning Journeys', partner_report: 'By Partner Report',
  vouchers: 'Vouchers', cert_prep: 'Cert Prep',
  jumpstart: 'Jumpstart', deal_ready: 'Deal Ready',
  scorm_cloud: 'SCORM Cloud', compete_hub: 'Compete Hub',
  skilling_box: 'Skilling in a Box', partner_dashboard: 'Partner Dashboard',
  mbr_dashboard: 'MBR Dashboard', esi_utilisation: 'ESI Utilisation',
};

// Build the navigation tree based on the user's scope.
// All flags default to TRUE when scope is empty (backward compat with ADMIN_USERS).
function buildNav(scope) {
  const s = scope || {};
  const portalRole  = s.portal_role;
  const allScope    = !s.type || s.type === 'all' || portalRole === 'tf_admin' || portalRole === 'tf_reporting_admin';
  const isPartner   = s.type === 'partner';
  const isPdm       = s.type === 'pdm' || portalRole === 'ms_pdm';
  const isProgAdmin = s.type === 'program_admin' || portalRole === 'ms_prog_admin';
  const showAdmin   = s.show_admin === true || allScope;
  const showJump    = s.show_jumpstart !== false || allScope;
  const showCert    = s.show_certification !== false || allScope;
  const showDR      = s.show_deal_ready !== false || allScope;
  const showAllUp   = s.show_all_up !== false || allScope;
  const showMBR     = !isPartner;
  const isTfUser    = s.is_tf_user === true || allScope;

  // Program Admin: build nav from their allowed pages ONLY.
  // Never fall through to the full nav — an empty page set yields an empty nav
  // (fail-closed) so a scoped user can never see pages outside their group.
  if (isProgAdmin) {
    const allowedPages = s.program_access?.pages || [];
    // scorm_cloud, compete_hub, skilling_box → LINKED PLATFORMS (not LevelUp Reports)
    const LINKED_KEYS = new Set(['compete_hub', 'skilling_box', 'scorm_cloud']);
    const LINKED_ICONS = { compete_hub: Globe, skilling_box: Package, scorm_cloud: Send };

    const reportsChildren = allowedPages
      .filter(key => PAGE_ROUTES[key] && !LINKED_KEYS.has(key) && key !== 'vouchers')
      .map(key => ({ label: PAGE_LABELS[key] || key, to: PAGE_ROUTES[key], icon: FileBarChart2 }));

    const linkedChildren = allowedPages
      .filter(key => LINKED_KEYS.has(key))
      .map(key => ({ label: PAGE_LABELS[key] || key, to: PAGE_ROUTES[key], icon: LINKED_ICONS[key] || Globe }));

    const nav = [];
    if (reportsChildren.length) nav.push({ label: 'LevelUp Reports', icon: FileBarChart2, children: reportsChildren, initialOpen: true });
    if (linkedChildren.length)  nav.push({ label: 'Linked Platforms', icon: Link, children: linkedChildren, initialOpen: true });
    return nav;
  }

  // ── SECTION 1: LEVELUP REPORTS ─────────────────────────────────────────────
  const levelupItems = [
    showAllUp && { label: 'All-Up LevelUp',    to: '/reports/allup',            icon: Users },
    { label: 'Course Activity',                to: '/reports/course-activity',  icon: BookOpen },
    { label: 'Learning Journeys',              to: '/reports/journeys',         icon: BookOpen },
    { label: 'Learner Activity',               to: '/reports/learner-activity', icon: Activity },
    showMBR && { label: 'MBR Dashboard',       to: '/reports/mbr',              icon: TrendingUp },
    showDR  && { label: 'Deal Ready',          to: '/reports/deal-ready',       icon: TrendingUp },
    showJump && { label: 'Jumpstart',          to: '/reports/jumpstart',        icon: Award },
    showCert && { label: 'Cert Prep',          to: '/reports/cert-prep',        icon: Award },
    isTfUser && { label: 'Vouchers',           to: '/reports/vouchers',         icon: Ticket },
  ].filter(Boolean);

  // ── SECTION 2: LINKED PLATFORMS ────────────────────────────────────────────
  const linkedItems = [
    { label: 'Compete Hub',          to: '/compete-hub',            icon: Globe },
    { label: 'Skilling in a Box',    to: '/reporting/skilling-box', icon: Package },
    { label: 'SCORM Cloud',          to: '/reports/scorm-cloud',    icon: Send  },
    { label: 'EventsUp',             to: '/linked-platforms/eventsup', icon: CalendarDays },
    { label: 'MyGoals', icon: Activity, comingSoon: true },
  ].filter(Boolean);

  // ── SECTION 3: INSIGHTS CUBE ───────────────────────────────────────────────
  const showFeeds = isTfUser;
  const insightsItems = [
    { label: 'Course Activity',   to: '/insights/course-activity', icon: BookOpen },
    { label: 'Catalog',           to: '/insights/catalog', icon: Library },
    showFeeds && { label: 'SCRMCld for MAQ', to: '/insights/scrmcld-maq', icon: Rss },
    showFeeds && { label: 'EventsUp for MAQ', to: '/insights/eventsup-maq', icon: CalendarDays },
    showFeeds && { label: 'Learning Journeys', to: '/insights/learning-journeys-maq', icon: Route },
    showFeeds && { label: 'Learning Journey Enrolments', to: '/insights/learning-journey-enrolments-maq', icon: UserCheck },
    { label: 'Compete Hub',       to: '/insights/compete-hub', icon: Globe },
    { label: 'Skilling in a Box', icon: Package,    comingSoon: true },
  ].filter(Boolean);

  const nav = [
    { label: 'LevelUp Reports', icon: FileBarChart2, children: levelupItems,  initialOpen: true  },
    { label: 'Linked Platforms', icon: Link,          children: linkedItems,   initialOpen: true  },
    { label: 'Insights Cube',   icon: Database,       children: insightsItems, initialOpen: false },
    showFeeds && { label: 'PSH Feeds', icon: Rss, initialOpen: false, children: [
      { label: 'Learners for PSH', to: '/psh-feeds/learners', icon: Users },
      { label: 'Courses for PSH', to: '/psh-feeds/courses', icon: GraduationCap },
      { label: 'Deal Ready for PSH', to: '/psh-feeds/deal-ready', icon: TrendingUp },
      { label: 'SCRMCld for PSH', to: '/psh-feeds/scrmcld-psh', icon: Send },
      { label: 'EventsUp for PSH', to: '/psh-feeds/eventsup-psh', icon: CalendarDays },
      { label: 'Learning Journeys', to: '/psh-feeds/learning-journeys', icon: Route },
      { label: 'Learning Journey Enrolments', to: '/psh-feeds/learning-journey-enrolments', icon: UserCheck },
    ] },
  ].filter(Boolean);

  // ── SECTION 4: ADMIN ────────────────────────────────────────────────────────
  if (showAdmin) {
    nav.push({
      label: 'Admin', icon: Settings, initialOpen: false,
      children: [
        { label: 'User Access',        to: '/admin/user-access',            icon: UserCog   },
        { label: 'Partner Dashboard',  to: '/reports/partner-self-service', icon: Building2 },
        { label: 'LMS Sync',           to: '/admin/sync',                   icon: RefreshCw },
        { label: 'Learners',           to: '/admin/learners',               icon: Users     },
        { label: 'Courses',            to: '/admin/courses',                icon: BookOpen  },
        { label: 'Learning Journeys',  to: '/admin/learning-journeys',      icon: BookOpen  },
        { label: 'ESI Utilisation',    to: '/reports/esi-utilisation',      icon: Users     },
        { label: 'LUP Portal Analytics', to: '/admin/portal/analytics',     icon: Activity  },
        { label: 'LUP Portal Feedback',  to: '/admin/portal/feedback',      icon: TrendingUp},
        { label: 'Access Audit',         to: '/admin/portal/access-audit',  icon: ShieldCheck },
      ],
    });
  }

  // ── SECTION 5: PARTNERS ─────────────────────────────────────────────────────
  const showPartners = s.is_tf_user === true || allScope;
  if (showPartners) {
    nav.push({
      label: 'Partners', icon: Building2, initialOpen: false,
      children: [
        { label: 'By Partner Report',   to: '/reports/by-partner',             icon: Building2 },
        { label: 'Overview',            to: '/partners',                       icon: Building2 },
        { label: 'ESI/MPL Partners',    to: '/partners/table/domain-config',   icon: Database  },
        { label: 'ESI Domains',         to: '/partners/table/allowed-domains', icon: Globe     },
        { label: 'Top 50 CSP Partners', to: '/partners/table/tiers',           icon: Award     },
        { label: 'Top 50 CSP Domains',  to: '/partners/table/tier-domains',    icon: Link      },
        { label: 'CSP Resellers',    to: '/partners/table/csp-resellers',   icon: Building2 },
        { label: 'MAICPP Domains',   to: '/partners/table/maicpp-domains',  icon: Database  },
        { label: 'MAICPP Registry',  to: '/partners/maicpp-registry',       icon: ShieldCheck },
        { label: 'Project Domains',  to: '/partners/table/project-domains', icon: Globe     },
        { label: 'Free Domains',     to: '/partners/table/free-domains',    icon: Globe     },
        { label: 'Blocked Domains',  to: '/partners/table/blocked-domains', icon: Database  },
      ],
    });
  }

  return nav;
}

// ── Nav section component ─────────────────────────────────────────────────────
function NavSection({ item }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(
    item.initialOpen !== false || (item.children || []).some(c => c.to && pathname.startsWith(c.to))
  );

  const navItemBase = {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '9px 16px', textDecoration: 'none',
    fontSize: 13, fontWeight: 500,
    borderRadius: 6, margin: '1px 8px',
    transition: 'background 0.15s, color 0.15s',
    cursor: 'pointer',
    color: '#B9DCD2',
    background: 'none', border: 'none',
    fontFamily: '"DM Sans", sans-serif',
    boxSizing: 'border-box', width: 'calc(100% - 16px)',
  };

  if (!item.children) {
    return (
      <NavLink
        to={item.to}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        style={({ isActive }) => ({
          ...navItemBase,
          color: isActive ? '#D4EC8E' : '#B9DCD2',
          background: isActive ? 'rgba(212,236,142,0.1)' : 'none',
          borderLeft: isActive ? '3px solid #D4EC8E' : '3px solid transparent',
        })}
      >
        <item.icon size={15} style={{ flexShrink: 0 }} />
        {item.label}
      </NavLink>
    );
  }

  return (
    <div>
      <button
        style={{
          ...navItemBase,
          justifyContent: 'flex-start',
          color: 'rgba(185,220,210,0.6)',
          fontSize: 10, fontWeight: 700,
          letterSpacing: '0.15em', textTransform: 'uppercase',
          padding: '8px 16px 4px',
          cursor: 'default',
          background: 'none', border: 'none',
          borderLeft: '3px solid transparent',
        }}
        onClick={() => setOpen(o => !o)}
        data-testid={`nav-section-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <item.icon size={13} />
        <span style={{ flex: 1, textAlign: 'left' }}>{item.label}</span>
        <ChevronRight
          size={11}
          style={{ transform: open ? 'rotate(90deg)' : '', transition: 'transform 0.2s' }}
        />
      </button>
      {open && (
        <div style={{ paddingLeft: 8 }}>
          {item.children.map(child =>
            child.comingSoon ? (
              /* ── Coming Soon item — non-interactive ───────────────────── */
              <div
                key={child.label}
                style={{
                  ...navItemBase,
                  opacity: 0.45,
                  pointerEvents: 'none',
                  cursor: 'default',
                  borderLeft: '3px solid transparent',
                }}
              >
                <child.icon size={14} style={{ flexShrink: 0 }} />
                <span style={{ flex: 1 }}>{child.label}</span>
                <span style={{
                  fontSize: 10, fontWeight: 600,
                  background: 'rgba(185,220,210,0.12)',
                  color: 'rgba(185,220,210,0.7)',
                  padding: '1px 7px', borderRadius: 10,
                  letterSpacing: '0.02em',
                }}>
                  Soon
                </span>
              </div>
            ) : (
              /* ── Regular nav item ─────────────────────────────────────── */
              <NavLink
                key={child.to}
                to={child.to}
                end={child.to === '/partners'}
                data-testid={`nav-${child.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                style={({ isActive }) => ({
                  ...navItemBase,
                  color: isActive ? '#D4EC8E' : '#B9DCD2',
                  background: isActive ? 'rgba(212,236,142,0.1)' : 'none',
                  borderLeft: isActive ? '3px solid #D4EC8E' : '3px solid transparent',
                })}
                onMouseEnter={e => {
                  if (!e.currentTarget.classList.contains('active')) {
                    e.currentTarget.style.color = '#fff';
                    e.currentTarget.style.background = '#1f5560';
                  }
                }}
                onMouseLeave={e => {
                  if (!e.currentTarget.style.borderLeftColor?.includes('D4EC8E')) {
                    e.currentTarget.style.color = '#B9DCD2';
                    e.currentTarget.style.background = 'none';
                  }
                }}
              >
                <child.icon size={14} style={{ flexShrink: 0 }} />
                {child.label}
              </NavLink>
            )
          )}
        </div>
      )}
    </div>
  );
}

// ── Layout ────────────────────────────────────────────────────────────────────
export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  useTelemetry(); // Stamp user_email on all page views and JS errors
  const [user, setUser] = useState({
    email: '',
    display_name: '',
    role_name: '',
    scope: {},
  });
  const [meLoaded, setMeLoaded]         = useState(false); // /api/auth/me resolved
  const [previewRole, setPreviewRole]   = useState(null);  // PREVIEW_ROLES item or null
  const [previewOpen, setPreviewOpen]   = useState(false); // dropdown open
  const dropdownRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/auth/me`, { withCredentials: true })
      .then(r => setUser({
        email: r.data.email || '',
        display_name: r.data.display_name || r.data.email || '',
        role_name: r.data.role_name || '',
        scope: r.data.scope || {},
      }))
      .catch(() => {})
      .finally(() => setMeLoaded(true));
  }, []);

  // Close preview dropdown when clicking outside
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setPreviewOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
    } catch (_e) { /* intentional — navigate regardless */ }
    navigate('/login', { replace: true });
  };

  const exitPreview = () => { setPreviewRole(null); setPreviewOpen(false); };

  // Use preview scope when active, otherwise real scope
  const activeScope = previewRole ? previewRole.scope : user.scope;
  const nav = useMemo(() => buildNav(activeScope), [activeScope]);

  // ── Real page-access enforcement (independent of the nav preview toggle) ────
  // Program-admin users may only reach the pages in their group's program_access.
  // A null result means "no restriction" (all pages allowed).
  const realScope = user.scope || {};
  const isRealProgAdmin = realScope.type === 'program_admin' || realScope.portal_role === 'ms_prog_admin';
  const allowedRoutes = useMemo(() => {
    if (!isRealProgAdmin) return null;
    return (realScope.program_access?.pages || [])
      .map(k => PAGE_ROUTES[k])
      .filter(Boolean);
  }, [isRealProgAdmin, realScope]);
  const firstAllowed = allowedRoutes && allowedRoutes.length ? allowedRoutes[0] : null;
  const pathAllowed = (pathname) => {
    if (!allowedRoutes) return true;
    return allowedRoutes.some(r => pathname === r || pathname.startsWith(r + '/'));
  };

  // Only TF Reporting Admins can use the preview toggle (ADMIN_USERS fallback also allowed)
  const isTfAdmin = user.scope.portal_role === 'tf_reporting_admin' || user.scope.type === 'all';

  const displayLabel = user.display_name || user.email.split('@')[0] || 'Admin';
  const roleLabel    = previewRole ? previewRole.label : user.role_name;

  return (
    <div className="app-shell">
      {/* ── Full-width top header ──────────────────────────────── */}
      <header className="topbar" data-testid="topbar" style={{ background: '#091F2C', borderBottom: '1px solid rgba(255,255,255,0.07)', zIndex: 100 }}>
        {/* Left: LevelUp brand — clickable → home */}
        <button
          onClick={() => navigate('/')}
          aria-label="Go to dashboard"
          style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        >
          <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 15, fontWeight: 700, color: '#fff', letterSpacing: '-0.3px' }}>
            Technofocus
          </span>
          <span style={{ color: 'rgba(255,255,255,0.25)', fontWeight: 300 }}>|</span>
          <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 15, fontWeight: 700, color: '#fff' }}>
            Level<span style={{ color: '#D4EC8E' }}>Up</span>
          </span>
          <span style={{ marginLeft: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
            <MicrosoftLogo />
            <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11 }}>Sponsored by Microsoft</span>
          </span>
        </button>

        {/* Right: preview toggle + user info + sign out */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>

            {/* Switch Role dropdown — TF Admins only */}
            {isTfAdmin && (
              <div ref={dropdownRef} style={{ position: 'relative' }}>
                <button
                  data-testid="preview-role-btn"
                  onClick={() => setPreviewOpen(o => !o)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    height: 30, padding: '0 12px',
                    background: previewRole ? 'rgba(251,191,36,0.2)' : 'rgba(255,255,255,0.07)',
                    border: previewRole ? '1px solid rgba(251,191,36,0.5)' : '1px solid rgba(255,255,255,0.12)',
                    borderRadius: 6, cursor: 'pointer',
                    color: previewRole ? '#fbbf24' : 'rgba(185,220,210,0.85)',
                    fontSize: 12, fontWeight: 600,
                    fontFamily: '"DM Sans", sans-serif',
                    transition: 'all 0.15s',
                  }}
                >
                  <Eye size={13} />
                  {previewRole ? `Previewing: ${previewRole.label}` : 'Preview as...'}
                  <ChevronDown size={11} style={{ transform: previewOpen ? 'rotate(180deg)' : '', transition: 'transform 0.2s' }} />
                </button>

                {previewOpen && (
                  <div
                    data-testid="preview-role-dropdown"
                    style={{
                      position: 'absolute', right: 0, top: 'calc(100% + 6px)',
                      background: '#0d2d38', border: '1px solid rgba(255,255,255,0.12)',
                      borderRadius: 10, minWidth: 270,
                      boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
                      zIndex: 1000, overflow: 'hidden',
                    }}
                  >
                    <div style={{ padding: '10px 14px 6px', fontSize: 10, fontWeight: 700, color: 'rgba(185,220,210,0.5)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                      Preview Nav As Role
                    </div>

                    {PREVIEW_ROLES.map(role => (
                      <button
                        key={role.id}
                        data-testid={`preview-role-option-${role.id}`}
                        onClick={() => { setPreviewRole(role); setPreviewOpen(false); }}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left',
                          padding: '10px 14px', background: previewRole?.id === role.id ? 'rgba(251,191,36,0.12)' : 'none',
                          border: 'none', cursor: 'pointer',
                          borderLeft: previewRole?.id === role.id ? '3px solid #fbbf24' : '3px solid transparent',
                          transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => { if (previewRole?.id !== role.id) e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                        onMouseLeave={e => { if (previewRole?.id !== role.id) e.currentTarget.style.background = 'none'; }}
                      >
                        <div style={{ color: previewRole?.id === role.id ? '#fbbf24' : '#e2e8f0', fontSize: 13, fontWeight: 600, fontFamily: '"DM Sans", sans-serif' }}>{role.label}</div>
                        <div style={{ color: 'rgba(185,220,210,0.5)', fontSize: 11, marginTop: 2 }}>{role.description}</div>
                      </button>
                    ))}

                    {previewRole && (
                      <>
                        <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '4px 0' }} />
                        <button
                          data-testid="exit-preview-dropdown-btn"
                          onClick={exitPreview}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 7,
                            width: '100%', textAlign: 'left', padding: '10px 14px',
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: '#f87171', fontSize: 13, fontWeight: 600,
                            fontFamily: '"DM Sans", sans-serif',
                            transition: 'background 0.15s',
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(248,113,113,0.08)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                        >
                          <X size={13} /> Exit Preview — Back to TF Admin
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Role badge */}
            {roleLabel && (
              <span
                data-testid="topbar-role-badge"
                style={{
                  background: previewRole ? 'rgba(251,191,36,0.15)' : 'rgba(212,236,142,0.15)',
                  color: previewRole ? '#fbbf24' : '#D4EC8E',
                  fontSize: 11, fontWeight: 700,
                  padding: '3px 10px', borderRadius: 20,
                  letterSpacing: '0.04em',
                  display: 'flex', alignItems: 'center', gap: 5,
                }}
              >
                <ShieldCheck size={11} />
                {roleLabel}
              </span>
            )}

            <span
              data-testid="topbar-user-email"
              style={{ color: '#B9DCD2', fontSize: 13, fontFamily: '"DM Sans", sans-serif' }}
            >
              {displayLabel}
            </span>
            <button
              onClick={handleLogout}
              data-testid="topbar-signout-btn"
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#D4EC8E', fontSize: 13, fontWeight: 600,
                fontFamily: '"DM Sans", sans-serif',
                padding: '4px 0',
                transition: 'opacity 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.opacity = '0.75'; }}
              onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
            >
              Sign Out
            </button>
          </div>
      </header>

      {/* ── Preview Role Banner (full width, below header) ──────── */}
      {previewRole && (
        <div
          data-testid="preview-role-banner"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '9px 24px',
            background: 'linear-gradient(90deg, #78350f, #92400e)',
            borderBottom: '1px solid rgba(251,191,36,0.3)',
            flexShrink: 0, zIndex: 99,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Eye size={14} color="#fbbf24" />
            <span style={{ fontSize: 13, fontWeight: 700, color: '#fef3c7' }}>
              Previewing navigation as: <span style={{ color: '#fbbf24' }}>{previewRole.label}</span>
            </span>
            <span style={{ fontSize: 12, color: '#fde68a', opacity: 0.8 }}>
              — {previewRole.description}. Your data access is unchanged.
            </span>
          </div>
          <button
            data-testid="exit-preview-banner-btn"
            onClick={exitPreview}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'rgba(251,191,36,0.2)', border: '1px solid rgba(251,191,36,0.4)',
              borderRadius: 6, padding: '4px 12px', cursor: 'pointer',
              color: '#fef3c7', fontSize: 12, fontWeight: 700,
              fontFamily: '"DM Sans", sans-serif',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(251,191,36,0.35)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(251,191,36,0.2)'; }}
          >
            <X size={12} /> Exit Preview
          </button>
        </div>
      )}

      {/* ── Body: sidebar + page content (squeezed between header/footer) ── */}
      <div className="app-body">
        {/* Sidebar */}
        <aside className="sidebar" data-testid="sidebar" style={{ background: '#194449' }}>
          {/* Nav */}
          <nav className="sidebar-nav" style={{ paddingTop: 8 }}>
            {nav.map((item, i) => <NavSection key={i} item={item} />)}
          </nav>

          {/* Footer: sign out */}
          <div className="sidebar-footer" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                width: 'calc(100% - 16px)', margin: '0 8px',
                padding: '9px 16px', background: 'none', border: 'none',
                cursor: 'pointer', borderRadius: 6,
                color: '#B9DCD2', fontSize: 13, fontWeight: 500,
                fontFamily: '"DM Sans", sans-serif',
                transition: 'color 0.15s, background 0.15s',
                boxSizing: 'border-box',
              }}
              onMouseEnter={e => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.background = '#1f5560'; }}
              onMouseLeave={e => { e.currentTarget.style.color = '#B9DCD2'; e.currentTarget.style.background = 'none'; }}
            >
              <LogOut size={15} />
              Sign Out
            </button>
          </div>
        </aside>

        {/* ── Main content ────────────────────────────────────── */}
        <div className="main-content">
          {/* Page content */}
          <main className="page-content" style={{ background: '#F4F3F5', display: 'flex', flexDirection: 'column' }}>
            <PreviewRoleContext.Provider value={previewRole}>
              <div style={{ flex: 1 }} data-testid="page-outlet">
                {!meLoaded ? (
                  <div
                    data-testid="content-loading"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}
                  >
                    <div style={{
                      width: 30, height: 30, borderRadius: '50%',
                      border: '3px solid #e2e8f0', borderTopColor: '#194449',
                      animation: 'spin 0.7s linear infinite',
                    }} />
                  </div>
                ) : isRealProgAdmin && !pathAllowed(location.pathname) ? (
                  firstAllowed ? (
                    <Navigate to={firstAllowed} replace />
                  ) : (
                    <div
                      data-testid="access-restricted-notice"
                      style={{
                        maxWidth: 520, margin: '80px auto', textAlign: 'center',
                        padding: 32, fontFamily: '"DM Sans", sans-serif', color: '#334155',
                      }}
                    >
                      <ShieldCheck size={40} color="#194449" style={{ marginBottom: 16 }} />
                      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8, color: '#091F2C' }}>
                        No pages assigned
                      </h2>
                      <p style={{ fontSize: 14, lineHeight: 1.6, color: '#64748b' }}>
                        Your group does not have access to any reporting pages yet.
                        Please contact your TechnoFocus administrator to be assigned report access.
                      </p>
                    </div>
                  )
                ) : (
                  <Outlet />
                )}
              </div>
            </PreviewRoleContext.Provider>
          </main>
        </div>
      </div>

      {/* ── Full-width footer ──────────────────────────────────── */}
      <LevelUpFooter />
    </div>
  );
}
